import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { renderSVG } from 'uqr';

const ADMIN_API = import.meta.env.VITE_API_BASE || '';
const QR_TOKEN_URL = `${ADMIN_API.replace(/\/+$/, '')}/qr-checkout/token`;
const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 min — half the 10-min token TTL

interface QRTokenResponse {
  success: boolean;
  qrToken: string;
  expiresAt: number;
  ttlSeconds: number;
}

export function QRCheckoutDisplay() {
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [ttl, setTtl] = useState<number>(600);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const fetchRef = useRef<number | null>(null);

  const fetchQR = useCallback(async () => {
    try {
      console.log('QRCheckoutDisplay: Starting fetchQR from URL:', QR_TOKEN_URL);
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('church_admin_token');
      console.log('QRCheckoutDisplay: Using auth token:', token ? `${token.substring(0, 15)}...` : 'null');

      const res = await fetch(QR_TOKEN_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      console.log('QRCheckoutDisplay: fetch response status:', res.status, res.ok);

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('QRCheckoutDisplay: fetch error details:', errText);
        throw new Error(
          res.status === 401
            ? 'Session expired. Please log in again.'
            : `Server error (${res.status}). Please try again.`
        );
      }

      const data: QRTokenResponse = await res.json();
      console.log('QRCheckoutDisplay: fetch response data:', data);

      if (!data.success) throw new Error('Failed to generate QR token.');

      console.log('QRCheckoutDisplay: Generating SVG with uqr.renderSVG');
      // uqr.renderSVG returns a full <svg> string — pure ESM, no Node built-ins
      const svg = renderSVG(data.qrToken, {
        ecc: 'H',         // high error correction
        border: 2,
      });
      console.log('QRCheckoutDisplay: SVG generated successfully. Length:', svg.length);
      
      setQrSvg(svg);
      setExpiresAt(data.expiresAt);
      setTtl(data.ttlSeconds);
      setSecondsLeft(data.ttlSeconds);
    } catch (err: any) {
      console.error('QRCheckoutDisplay: Error during fetchQR:', err);
      setError(err.message || 'Failed to load QR code.');
    } finally {
      console.log('QRCheckoutDisplay: fetchQR completed (finally block)');
      setLoading(false);
    }
  }, []);

  // Initial fetch + rotation timer
  useEffect(() => {
    fetchQR();
    fetchRef.current = window.setInterval(fetchQR, REFRESH_INTERVAL_MS);
    return () => {
      if (fetchRef.current) clearInterval(fetchRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchQR]);

  // Live 1-second countdown
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!expiresAt) return;
    timerRef.current = window.setInterval(() => {
      setSecondsLeft(Math.max(0, expiresAt - Math.floor(Date.now() / 1000)));
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [expiresAt]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const r = 54;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - secondsLeft / Math.max(ttl, 1));
  const expiringSoon = secondsLeft < 60;

  return (
    <div
      ref={containerRef}
      className={`flex flex-col items-center justify-center gap-6 p-8 bg-white ${isFullscreen ? 'min-h-screen' : ''}`}
    >
      {/* Title */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">QR Pickup</h2>
        <p className="text-sm text-gray-500 mt-1">
          Parents scan once to check out <strong>all</strong> their children
        </p>
      </div>

      {/* QR + countdown ring */}
      <div className="relative flex items-center justify-center">
        {!loading && !error && (
          <svg
            className="absolute pointer-events-none"
            width="272" height="272"
            viewBox="0 0 120 120"
            style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <circle cx="60" cy="60" r={r} fill="none" stroke="#e5e7eb" strokeWidth="4" />
            <circle
              cx="60" cy="60" r={r}
              fill="none"
              stroke={expiringSoon ? '#ef4444' : '#22c55e'}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: '60px 60px',
                transition: 'stroke-dashoffset 1s linear, stroke 0.5s',
              }}
            />
          </svg>
        )}

        <div className="relative z-10 w-52 h-52 flex items-center justify-center">
          {loading ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 gap-3">
              <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Generating…</p>
            </div>
          ) : error ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 rounded-2xl border-2 border-red-200 gap-3 px-4 text-center">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
              <button onClick={fetchQR} className="text-sm text-primary-600 underline font-medium">
                Retry
              </button>
            </div>
          ) : qrSvg ? (
            // Render uqr's SVG output directly as an inline image
            <img
              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvg)}`}
              alt="QR code for child pickup"
              className="w-full h-full rounded-2xl shadow-lg"
              style={{ imageRendering: 'pixelated' }}
            />
          ) : null}
        </div>
      </div>

      {/* Countdown pill */}
      {!loading && !error && (
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
          expiringSoon ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
        }`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {expiringSoon ? 'Refreshing soon — ' : 'Valid for '}
          <span className="tabular-nums font-mono">{minutes}:{String(seconds).padStart(2, '0')}</span>
        </div>
      )}

      {/* Instructions */}
      <div className="max-w-xs text-center space-y-1">
        <p className="text-xs text-gray-500">
          Parents open <strong>Shephard</strong> → Check-In tab → tap{' '}
          <strong>"Scan to Pick Up"</strong>
        </p>
        <p className="text-xs text-gray-400">
          Rotates every 5 min. Multiple parents can scan simultaneously.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={fetchQR}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh Now
        </button>
        <button
          onClick={toggleFullscreen}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isFullscreen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            }
          </svg>
          {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
      </div>
    </div>
  );
}
